import { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { InAppBrowser } from '@capacitor/inappbrowser';
import { Toaster } from 'sonner';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import AdminDashboard from './pages/AdminDashboard';
import Profile from './pages/Profile';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import '@/App.css';

function ProtectedRoute({ children }) {
  const token = localStorage.getItem('token');
  return token ? children : <Navigate to="/login" replace />;
}

function NativeAppUrlListener() {
  const navigate = useNavigate();
  const checkedLaunchUrlRef = useRef(false);
  const handledUrlsRef = useRef(new Set());

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return undefined;
    }

    const handleOpenUrl = ({ url }) => {
      if (!url) return;
      if (handledUrlsRef.current.has(url)) return;

      try {
        const parsed = new URL(url);
        const routePath = `/${parsed.host}${parsed.pathname}${parsed.search}${parsed.hash}`;

        if (routePath.startsWith('/payment/')) {
          handledUrlsRef.current.add(url);
          InAppBrowser.close().catch(() => {});
          Browser.close().catch(() => {});
          navigate(routePath, { replace: true });
        }
      } catch (error) {
        console.error('Failed to process app URL:', error);
      }
    };

    let listenerHandle;

    CapacitorApp.addListener('appUrlOpen', handleOpenUrl).then((handle) => {
      listenerHandle = handle;
    });

    if (!checkedLaunchUrlRef.current) {
      checkedLaunchUrlRef.current = true;
      CapacitorApp.getLaunchUrl().then((launchData) => {
        if (launchData?.url) {
          handleOpenUrl(launchData);
        }
      });
    }

    return () => {
      listenerHandle?.remove();
    };
  }, [navigate]);

  return null;
}

function PaymentReturnBridge() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      return undefined;
    }

    const params = new URLSearchParams(location.search);
    const appReturn = params.get('app_return') === '1';
    const paymentId = params.get('payment_id');
    const isCancelPath = location.pathname === '/payment/cancel';
    const isSuccessPath = location.pathname === '/payment/success';
    const shouldBridgeSuccess = appReturn && Boolean(paymentId) && isSuccessPath;
    const shouldBridgeCancel = appReturn && isCancelPath;

    if (!shouldBridgeSuccess && !shouldBridgeCancel) {
      return undefined;
    }

    const targetPath = shouldBridgeCancel ? 'payment/cancel' : 'payment/success';
    const targetUrl = /android/i.test(window.navigator.userAgent)
      ? `intent://${targetPath}${location.search}#Intent;scheme=app.caddiepay.mobile;package=app.caddiepay.mobile;S.browser_fallback_url=${encodeURIComponent(window.location.href)};end`
      : `app.caddiepay.mobile://${targetPath}${location.search}`;
    const marker = `topup-return-bridge:${targetUrl}`;

    if (sessionStorage.getItem(marker) === 'done') {
      return undefined;
    }

    sessionStorage.setItem(marker, 'done');

    const fallbackTimer = window.setTimeout(() => {
      const fallbackPath = shouldBridgeCancel
        ? `/payment/cancel${location.search}`
        : `/payment/success${location.search}`;

      if (`${location.pathname}${location.search}` !== fallbackPath) {
        navigate(fallbackPath, { replace: true });
      }
    }, 1500);

    window.location.href = targetUrl;

    return () => {
      window.clearTimeout(fallbackTimer);
    };
  }, [location.pathname, location.search, navigate]);

  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <NativeAppUrlListener />
        <PaymentReturnBridge />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            } 
          />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route 
            path="/profile" 
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } 
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
      <PWAInstallPrompt />
    </div>
  );
}

export default App;
